export const calculatePercentageValue = (a: bigint, b: bigint) => {
  if (b <= 0) {
    return 0
  }

  return Number((a * BigInt(100)) / b)
}

export const calculatePercentageValueWithDecimals = (a: number, b: number) => {
  if (b <= 0) {
    return 0
  }

  return Number((a * 100) / b).toFixed(1)
}

export const calculateFeeValue = (value: bigint, fee: bigint): bigint => {
  return (value * fee) / BigInt(10000)
}

export const splitBn = (value: bigint, length: number): bigint[] => {
  if (!length) {
    return []
  }

  const quotient = value / BigInt(length)
  const remainder = value % BigInt(length)
  const values = new Array(length).fill(quotient)

  values[values.length - 1] = values[values.length - 1] + remainder

  return values
}

export const sumBn = (value: bigint[]) => {
  return value.reduce((acc, curr) => acc + curr, BigInt(0))
}

export const convertDecimalsBN = (value: bigint, decimals = 18): bigint => {
  return value / BigInt(10 ** decimals)
}

export const convertDecimals = (value: number | bigint, decimals = 18): number => {
  if (typeof value === 'bigint') {
    // Use BigInt division for precision, similar to convertWeiToTokens
    const divisor = BigInt(10 ** decimals)
    const quotient = value / divisor
    const remainder = value % divisor

    // If there's a remainder, handle the decimal part
    if (remainder === 0n) {
      return Number(quotient)
    }

    // For cases with decimals, use string manipulation to avoid precision errors
    const quotientStr = quotient.toString()
    const remainderStr = remainder.toString().padStart(decimals, '0')

    const trimmedRemainder = remainderStr.replace(/0+$/, '')

    if (trimmedRemainder === '') {
      return Number(quotientStr)
    }

    return parseFloat(`${quotientStr}.${trimmedRemainder}`)
  }

  // For number type, use the original implementation
  return Number(value) / 10 ** decimals
}

export const roundBigNumber = (
  value: bigint,
  decimals: number = 18,
  strategy: 'up' | 'down' | 'nearest',
): bigint => {
  const decimalsNum = Number(decimals)
  if (
    typeof decimals !== 'number' ||
    isNaN(decimalsNum) ||
    !isFinite(decimalsNum) ||
    decimalsNum < 0 ||
    !Number.isInteger(decimalsNum)
  ) {
    throw new Error('Decimals must be a non-negative integer.')
  }

  if (!['up', 'down', 'nearest'].includes(strategy)) {
    throw new Error('Invalid rounding strategy. Must be "up", "down", or "nearest".')
  }

  // Use string-based approach to create 10^decimals as BigInt to avoid any type mixing
  const decimalsInt = Math.floor(decimalsNum)

  // This avoids any potential type mixing issues
  const powerString = decimalsInt === 0 ? '1' : '1' + '0'.repeat(decimalsInt)
  const power = BigInt(powerString)

  let roundedValue: bigint

  if (strategy === 'up') {
    roundedValue = ((value + (power - 1n)) / power) * power
  } else if (strategy === 'down') {
    roundedValue = (value / power) * power
  } else if (strategy === 'nearest') {
    const halfPower = power / 2n
    roundedValue = ((value + halfPower) / power) * power
  } else {
    throw new Error('Invalid rounding strategy.')
  }

  return roundedValue
}

export function cutDecimalsWithoutRounding(value: bigint | number, digits = 3): number {
  if (value === undefined || value === null) {
    return 0
  }

  const valueStr = value.toString()

  if (!valueStr.includes('.')) {
    return Number(value)
  }

  const [integers, decimals] = valueStr.trim().split('.')

  const numberConcatenated = `${integers}.${decimals.substring(0, digits)}`

  return parseFloat(numberConcatenated)
}

export const roundToFirstSignificantDigit = (
  value: number,
  {
    maxDecimalPlaces = 2,
    significantDigits = 1,
  }: {
    maxDecimalPlaces?: number
    significantDigits?: number
  } = {},
): number => {
  if (value === 0) return 0
  if (value >= 0.02) {
    return parseFloat(value.toFixed(maxDecimalPlaces))
  }
  if (value <= -0.02) {
    return parseFloat(value.toFixed(maxDecimalPlaces))
  }

  const absoluteValue = Math.abs(value)
  const decimalPlacesStr = String(absoluteValue).split('.')[1]
  let zerosCountInNumber = 0

  for (let i = 0; i < decimalPlacesStr.length; i++) {
    if (decimalPlacesStr[i] === '0') {
      zerosCountInNumber++
    } else {
      break
    }
  }

  if (value < 0) {
    return parseFloat(`${value.toFixed(zerosCountInNumber + significantDigits)}`)
  }

  return parseFloat(value.toFixed(zerosCountInNumber + significantDigits))
}
